import { loadCms } from "./cms/store.js";
import { resolveHomeSeoDescription } from "./cms/seo-description.js";

const SITE = {
  name: "בורגרטיים",
  phone: "*2395",
  locale: "he_IL",
  sameAs: [
    "https://www.facebook.com/burgertimeisrael",
    "https://www.instagram.com/burger_time10/",
  ],
};

function getCanonicalUrl() {
  let path = window.location.pathname;
  if (path.endsWith("/")) path += "index.html";
  if (!path || path === "/") path = "/index.html";
  return `${window.location.origin}${path}`;
}

function toAbsoluteUrl(relativePath) {
  try {
    return new URL(relativePath, window.location.href).href;
  } catch {
    return relativePath;
  }
}

function setMeta(name, content, { property = false } = {}) {
  if (!content) return;
  const attr = property ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = href;
}

function injectJsonLd(data) {
  document.querySelectorAll('script[data-seo-jsonld="1"]').forEach((node) => node.remove());
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.dataset.seoJsonld = "1";
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function syncSocialMeta() {
  const canonical = getCanonicalUrl();
  setCanonical(canonical);
  setMeta("og:url", canonical, { property: true });

  const ogImage = document.querySelector('meta[property="og:image"]')?.content;
  if (ogImage) {
    const absolute = toAbsoluteUrl(ogImage);
    setMeta("og:image", absolute, { property: true });
    setMeta("twitter:image", absolute);
  }
}

function restaurantSchema(extra = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    name: SITE.name,
    servesCuisine: "בשרי, בורגרים, אמריקאי",
    telephone: SITE.phone,
    sameAs: SITE.sameAs,
    ...extra,
  };
}

function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: toAbsoluteUrl(item.href),
    })),
  };
}

function injectPageSchema(page, { homeDescription } = {}) {
  const origin = window.location.origin;

  if (page === "home") {
    const description =
      homeDescription ??
      "רשת בורגרטיים — 6 סניפים בשומרון, בורגרים בשריים על הפלנצ'ה ומשלוחים מהירים.";
    injectJsonLd([
      restaurantSchema({
        url: `${origin}/index.html`,
        description,
        image: toAbsoluteUrl("../pic/logoround.png"),
      }),
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE.name,
        url: `${origin}/index.html`,
        inLanguage: "he-IL",
      },
    ]);
    return;
  }

  if (page === "menu") {
    injectJsonLd([
      breadcrumbSchema([
        { name: "דף הבית", href: "index.html" },
        { name: "התפריט", href: "menu.html" },
      ]),
      restaurantSchema({ url: `${origin}/menu.html`, menu: `${origin}/menu.html` }),
    ]);
    return;
  }

  if (page === "branches") {
    injectJsonLd([
      breadcrumbSchema([
        { name: "דף הבית", href: "index.html" },
        { name: "סניפים", href: "branches.html" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "סניפי בורגרטיים",
        itemListElement: [
          "קרני שומרון",
          "טלמון",
          "בית אל",
          "תפוח",
          "אריאל",
          "יקיר",
        ].map((name, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name,
        })),
      },
    ]);
    return;
  }

  if (page === "contact") {
    injectJsonLd([
      breadcrumbSchema([
        { name: "דף הבית", href: "index.html" },
        { name: "צרו קשר", href: "contact.html" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "צרו קשר | בורגרטיים",
        url: `${origin}/contact.html`,
      },
    ]);
  }
}

export function syncHomeSeoMeta(cms = loadCms()) {
  if (document.body.dataset.page !== "home") return;

  const description = resolveHomeSeoDescription(cms);
  setMeta("description", description);
  setMeta("og:description", description, { property: true });
  setMeta("twitter:description", description);
  injectPageSchema("home", { homeDescription: description });
}

export function initSeo() {
  if (document.body.dataset.page === "admin") return;

  syncSocialMeta();
  const page = document.body.dataset.page;
  if (page === "home") {
    syncHomeSeoMeta();
    return;
  }
  injectPageSchema(page);
}

export function updateBranchSeo(branch) {
  const title = `${branch.title} | בורגרטיים`;
  const description = `סניף ${branch.branch} — ${branch.address}. שעות פתיחה: ${branch.time}. התקשרו ${SITE.phone}.`;

  document.title = title;
  setMeta("description", description);
  setMeta("og:title", title, { property: true });
  setMeta("og:description", description, { property: true });
  setMeta("twitter:title", title);
  setMeta("twitter:description", description);

  if (branch.image) {
    const imageUrl = toAbsoluteUrl(branch.image);
    setMeta("og:image", imageUrl, { property: true });
    setMeta("og:image:alt", branch.title, { property: true });
    setMeta("twitter:image", imageUrl);
  }

  syncSocialMeta();

  injectJsonLd([
    breadcrumbSchema([
      { name: "דף הבית", href: "index.html" },
      { name: "סניפים", href: "branches.html" },
      { name: branch.branch, href: "branch.html" },
    ]),
    restaurantSchema({
      name: branch.title,
      url: getCanonicalUrl(),
      description,
      address: {
        "@type": "PostalAddress",
        streetAddress: branch.address,
        addressCountry: "IL",
      },
      openingHours: branch.time,
      image: branch.image ? toAbsoluteUrl(branch.image) : undefined,
    }),
  ]);
}
